import { useState } from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Link,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { ApiError } from '@/auth/api';
import { useAuth } from '@/auth/useAuth';
import { PrimaryButton } from '@/components/PrimaryButton';
import { completeInternalReservation } from '@/features/operations/api';
import { panelPaymentMethodOptions, paymentMethodLabels } from '@/features/operations/labels';
import {
  priceDifferenceLabel,
  priceError,
  priceInputToMinor,
  toPriceInput,
} from '@/features/operations/money';
import { usePriceDisplay } from '@/features/operations/PriceDisplayProvider';
import type {
  ActiveReservation,
  CompleteReservationInput,
  DeviceDetail,
} from '@/features/operations/types';

type PaymentOption = CompleteReservationInput['paymentMethod'];
type FieldErrors = { finalPrice?: string; paymentMethod?: string; saleDate?: string };

const today = () => new Date().toLocaleDateString('sv-SE');

/**
 * Concrete the sale of a reservation from the device sheet (VEN-4, P-E4): the buyer came, paid
 * and takes the phone. The final price starts at the reserved one and may be changed; the
 * payment method and the date are asked for. The buyer is the one who reserved.
 *
 * The dialog is the confirmation (UC-031): it says the warranty in force is frozen and the
 * device becomes Vendido before saving.
 */
export function CompleteReservationDialog({
  open,
  token,
  deviceId,
  publishedPriceMinor,
  reservation,
  onClose,
  onCompleted,
}: {
  open: boolean;
  token: string;
  deviceId: string;
  publishedPriceMinor: number | null;
  reservation: Pick<ActiveReservation, 'id' | 'reference' | 'buyer' | 'account' | 'priceMinor'>;
  /// `stale` is true when the reservation turned out to have changed.
  onClose: (stale: boolean) => void;
  onCompleted: (device: DeviceDetail) => void;
}) {
  const { formatNullablePrice } = usePriceDisplay();
  const { user } = useAuth();
  const [finalPrice, setFinalPrice] = useState(() => toPriceInput(reservation.priceMinor));
  const [paymentMethod, setPaymentMethod] = useState<PaymentOption | ''>('');
  const [saleDate, setSaleDate] = useState(today);
  const [errors, setErrors] = useState<FieldErrors>({});
  const [isSaving, setIsSaving] = useState(false);
  const [stale, setStale] = useState(false);
  const [requestError, setRequestError] = useState<string | null>(null);
  const [warrantyMissing, setWarrantyMissing] = useState(false);

  const reset = () => {
    setFinalPrice(toPriceInput(reservation.priceMinor));
    setPaymentMethod('');
    setSaleDate(today());
    setErrors({});
    setStale(false);
    setRequestError(null);
    setWarrantyMissing(false);
  };

  const close = () => {
    if (isSaving) return;
    const wasStale = stale;
    reset();
    onClose(wasStale);
  };

  const difference = priceDifferenceLabel(
    priceInputToMinor(finalPrice),
    publishedPriceMinor,
    formatNullablePrice,
  );

  const handleComplete = async () => {
    const problems: FieldErrors = {};
    const priceProblem = priceError(finalPrice, 'El precio final');
    if (priceProblem) problems.finalPrice = priceProblem;
    if (!paymentMethod) problems.paymentMethod = 'Elegí cómo pagó.';
    if (!saleDate) problems.saleDate = 'Elegí la fecha de la venta.';
    // `YYYY-MM-DD` compares as text; a sale is recorded once it happened.
    else if (saleDate > today()) problems.saleDate = 'La fecha de venta no puede ser futura.';
    setErrors(problems);
    setRequestError(null);
    setWarrantyMissing(false);
    if (Object.keys(problems).length || !paymentMethod) return;

    setIsSaving(true);

    try {
      const updated = await completeInternalReservation(token, deviceId, reservation.id, {
        finalPrice: finalPrice.trim(),
        paymentMethod,
        saleDate,
      });
      reset();
      onCompleted(updated);
    } catch (cause) {
      const code = cause instanceof ApiError ? cause.details?.code : undefined;

      if (code === 'WARRANTY_NOT_CONFIGURED') {
        setWarrantyMissing(true);
      } else if (code === 'RESERVATION_EXPIRED') {
        setRequestError(
          'La reserva venció y ya no se puede concretar. Cancelala para liberar el equipo.',
        );
      } else if (cause instanceof ApiError && (cause.status === 409 || cause.status === 404)) {
        setStale(true);
      } else {
        setRequestError('No pudimos registrar la venta. Volvé a intentar.');
      }
    } finally {
      setIsSaving(false);
    }
  };

  const disabled = isSaving || stale || !reservation.account;

  return (
    <Dialog open={open} onClose={close} maxWidth="sm" fullWidth>
      <DialogTitle>Concretar la venta a {reservation.buyer.name}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          {reservation.account ? (
            <Alert severity="info">
              La venta quedará en la cuenta de{' '}
              <Link
                component={RouterLink}
                to={`/admin/customers/${reservation.account.id}`}
                target="_blank"
                rel="noopener"
              >
                {reservation.buyer.name}
              </Link>
              . El contacto de la reserva queda congelado.
            </Alert>
          ) : (
            <Alert severity="warning">
              Esta reserva antigua no tiene cuenta compradora. No se puede concretar desde el panel.
            </Alert>
          )}
          {stale ? (
            <Alert severity="error">
              La reserva ya no está activa: otra persona la concretó o la canceló recién. Cerrá este
              diálogo para ver cómo quedó el equipo.
            </Alert>
          ) : null}
          {requestError ? <Alert severity="error">{requestError}</Alert> : null}
          {warrantyMissing ? (
            <Alert severity="error">
              Falta configurar la garantía, que se guarda con cada venta.{' '}
              {/* Only an ADMIN enters Configuración comercial; an employee is told who can. */}
              {user?.role === 'ADMIN' ? (
                <>
                  Cargala en{' '}
                  <Link component={RouterLink} to="/admin/pricing">
                    Configuración comercial
                  </Link>{' '}
                  y volvé a confirmar.
                </>
              ) : (
                'Un administrador la carga en Configuración comercial.'
              )}
            </Alert>
          ) : null}
          <Typography variant="body2">
            Cierra la reserva #{reservation.reference}: el equipo pasa a <strong>Vendido</strong> y
            se guarda la garantía configurada hoy.
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                autoFocus
                fullWidth
                required
                label="Precio final (USD)"
                value={finalPrice}
                disabled={disabled}
                inputProps={{ inputMode: 'decimal' }}
                error={Boolean(errors.finalPrice)}
                helperText={
                  errors.finalPrice ??
                  `Reservado a ${formatNullablePrice(reservation.priceMinor)}. ${difference ?? ''}`.trim()
                }
                onChange={(event) => {
                  setFinalPrice(event.target.value);
                  setErrors((current) => ({ ...current, finalPrice: undefined }));
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                select
                fullWidth
                required
                label="Medio de pago"
                value={paymentMethod}
                disabled={disabled}
                error={Boolean(errors.paymentMethod)}
                helperText={errors.paymentMethod ?? ' '}
                onChange={(event) => {
                  setPaymentMethod(event.target.value as PaymentOption);
                  setErrors((current) => ({ ...current, paymentMethod: undefined }));
                }}
              >
                {panelPaymentMethodOptions.map((option) => (
                  <MenuItem key={option} value={option}>
                    {paymentMethodLabels[option]}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                type="date"
                label="Fecha de venta"
                value={saleDate}
                disabled={disabled}
                InputLabelProps={{ shrink: true }}
                inputProps={{ max: today() }}
                error={Boolean(errors.saleDate)}
                helperText={errors.saleDate ?? 'La garantía corre desde esta fecha.'}
                onChange={(event) => {
                  setSaleDate(event.target.value);
                  setErrors((current) => ({ ...current, saleDate: undefined }));
                }}
              />
            </Grid>
          </Grid>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button color="inherit" disabled={isSaving} onClick={close}>
          {stale ? 'Cerrar' : 'Volver'}
        </Button>
        {stale ? null : (
          <PrimaryButton disabled={disabled} onClick={() => void handleComplete()}>
            {isSaving ? 'Registrando...' : 'Confirmar venta'}
          </PrimaryButton>
        )}
      </DialogActions>
    </Dialog>
  );
}
